import {
  allowedStageMoves,
  ATTENTION_STATE_LABELS,
  type DealItem,
  FINANCING_LABELS,
  FinancingType,
  type InteractionItem,
  InterestLevel,
  type LeadDetail,
  MANAGER_ROLES,
  type PipelineStage,
  PURCHASE_HORIZON_LABELS,
  PURCHASE_PURPOSE_LABELS,
  PurchaseHorizon,
  PurchasePurpose,
  QUOTE_STATUS_LABELS,
  type QuoteItem,
  stageUndoKind,
  WRITE_ROLES,
} from '@performance-crm/shared';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Bot,
  CalendarClock,
  CalendarPlus,
  Check,
  ClipboardList,
  Copy,
  Eye,
  FileText,
  Mail,
  MessageCircle,
  PauseCircle,
  Pencil,
  Phone,
  RotateCcw,
  ShieldCheck,
  ShieldOff,
  StickyNote,
  Tag,
  Undo2,
  UserCog,
  XCircle,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnnulQuoteDialog } from '@/components/commercial/AnnulQuoteDialog';
import { DealActionDialog, type DealActionMode } from '@/components/commercial/DealActionDialog';
import { EditDealDialog } from '@/components/commercial/EditDealDialog';
import { EditQuoteDialog } from '@/components/commercial/EditQuoteDialog';
import { QuoteDialog } from '@/components/commercial/QuoteDialog';
import { AttachmentsCard } from '@/components/leads/AttachmentsCard';
import { CustomFieldsCard } from '@/components/leads/CustomFieldsCard';
import { TagChip } from '@/components/leads/TagChips';
import { TagsDialog } from '@/components/leads/TagsDialog';
import { ContactEditDialog } from '@/components/leads/ContactEditDialog';
import { MergeContactsDialog } from '@/components/leads/MergeContactsDialog';
import { ConsentDialog, OptOutDialog } from '@/components/leads/OptOutDialog';
import { OPT_OUT_NOTICE } from '@/components/leads/QuickContactButtons';
import { ConversationPanel } from '@/components/leads/ConversationPanel';
import { WhatsappMenu } from '@/components/leads/WhatsappMenu';
import { StageMoveDialog } from '@/components/pipeline/StageMoveDialog';
import { StageUndoDialog } from '@/components/pipeline/StageUndoDialog';
import {
  AttentionBadge,
  BotBadge,
  FollowupBadge,
  ScoreBadge,
  StageBadge,
  ContactBadge,
} from '@/components/leads/badges';
import {
  AnnulVisitDialog,
  AssignDialog,
  CancelTaskDialog,
  CompleteTaskDialog,
  ConfirmAppointmentDialog,
  DesistDialog,
  InteractionDialog,
  ReopenDialog,
  RescheduleDialog,
  type AttendanceOutcome,
} from '@/components/leads/dialogs';
import { appointmentDue } from '@/components/leads/appointment';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import {
  fmt,
  INTEREST_LABEL,
  STAGE_LABEL,
  telLink,
  TYPE_LABEL,
  waLink,
  describeAppointment,
} from '@/lib/format';
import {
  useAdvisors,
  useAttentionAction,
  useCatalogs,
  useLead,
  useSetConsent,
  useUpdateLead,
  useConfirmAppointment,
} from '@/lib/queries';

type DialogKind =
  | 'log'
  | 'schedule'
  | 'complete'
  | 'attendance'
  | 'annul-visit'
  | 'reschedule'
  | 'cancel'
  | 'assign'
  | 'desist'
  | 'reopen'
  | 'quote'
  | 'undo'
  | 'tags'
  | null;

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const { data: lead, isLoading, error } = useLead(id);
  const { data: advisors } = useAdvisors(lead?.companyId);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [task, setTask] = useState<InteractionItem | null>(null);
  // E1: «Confirmar asistencia» abre el diálogo sin opción elegida; «No asistió» lo abre con esa opción.
  const [attendance, setAttendance] = useState<AttendanceOutcome | null>(null);
  const [moveStage, setMoveStage] = useState<PipelineStage | null>(null);
  // `next: 'DESIST'` = «Devolver y desistir» (la devolución sale con el desistimiento como siguiente paso).
  const [dealAction, setDealAction] = useState<{
    deal: DealItem;
    mode: DealActionMode;
    next?: 'TASK' | 'DESIST';
  } | null>(null);
  // Correcciones y anulación de proforma con diálogos propios (Lote 5 · H4, P14 y P26).
  const [editDeal, setEditDeal] = useState<DealItem | null>(null);
  const [editQuote, setEditQuote] = useState<QuoteItem | null>(null);
  const [annulQuote, setAnnulQuote] = useState<QuoteItem | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }
  if (error || !lead) {
    return (
      <div className="space-y-3">
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {error ? (error as Error).message : 'Lead no encontrado'}
        </p>
        <Button asChild variant="outline">
          <Link to="/leads">
            <ArrowLeft className="h-4 w-4" /> Volver a leads
          </Link>
        </Button>
      </div>
    );
  }

  // Solo lectura (P18): el asesor de captación ve su lead tras el traspaso, sin ninguna acción.
  const readOnly = lead.readOnly;
  const isManager =
    !readOnly &&
    !!user?.memberships.some(
      (m) => m.companyId === lead.companyId && MANAGER_ROLES.includes(m.role),
    );
  const canWrite =
    !readOnly &&
    !!user?.memberships.some((m) => m.companyId === lead.companyId && WRITE_ROLES.includes(m.role));
  const active = lead.status === 'ACTIVO' && !lead.hasDesisted && !readOnly;
  // Quién devuelve (asesor dueño del lead o quien gestiona) y quién anula (solo quien gestiona): P16.
  const canRefund = canWrite && (isManager || (!!user && lead.advisorId === user.id));
  const canAnnul = isManager;
  const liveDeals = lead.deals.filter((d) => d.state === 'ACTIVO');
  // Con venta viva se devuelve la venta (arrastra su separación); si no, la separación.
  const primaryLiveDeal = liveDeals.find((d) => d.type === 'VENTA') ?? liveDeals[0] ?? null;
  const tel = telLink(lead.contact.phoneNormalized);
  const wa = waLink(lead.contact.phoneNormalized, lead.contact.fullName, lead.projectName);
  const openTask = (kind: DialogKind, t: InteractionItem) => {
    setTask(t);
    setDialog(kind);
  };
  const openAttendance = (t: InteractionItem, outcome: AttendanceOutcome | null) => {
    setAttendance(outcome);
    openTask('attendance', t);
  };

  return (
    <div className="space-y-4">
      <Link
        to="/leads"
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Leads
      </Link>

      <Header
        lead={lead}
        active={active}
        readOnly={readOnly}
        isManager={isManager}
        canWrite={canWrite}
        liveDeal={primaryLiveDeal}
        canRefund={canRefund}
        tel={tel}
        wa={wa}
        onAction={setDialog}
        onRefundAndDesist={() =>
          primaryLiveDeal &&
          setDealAction({ deal: primaryLiveDeal, mode: 'refund', next: 'DESIST' })
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <NextTaskPanel
            lead={lead}
            active={active}
            onComplete={(t) => openTask('complete', t)}
            onAttendance={openAttendance}
            onReschedule={(t) => openTask('reschedule', t)}
            onCancel={(t) => openTask('cancel', t)}
            onSchedule={() => setDialog('schedule')}
          />
          <Timeline
            lead={lead}
            readOnly={readOnly}
            onComplete={(t) => openTask('complete', t)}
            onAttendance={(t) => openAttendance(t, null)}
            onCancel={(t) => openTask('cancel', t)}
            onAnnulVisit={(t) => openTask('annul-visit', t)}
          />
          {!readOnly && (
            <ConversationPanel
              leadId={lead.id}
              companyId={lead.companyId}
              canSend={active}
              optedOut={!lead.contact.consent.contactable}
              isManager={isManager}
              botAttending={lead.attention?.owner === 'BOT'}
            />
          )}
          <HistoryCard lead={lead} />
        </div>
        <div className="space-y-4">
          <AttentionCard lead={lead} active={active} isManager={isManager} />
          <CommercialCard
            lead={lead}
            active={active}
            canRefund={canRefund}
            canAnnul={canAnnul}
            canEditQuotes={canWrite}
            onNewQuote={() => setDialog('quote')}
            onMove={setMoveStage}
            onUndo={() => setDialog('undo')}
            onDealAction={(deal, mode) => setDealAction({ deal, mode })}
            onEditDeal={setEditDeal}
            onEditQuote={setEditQuote}
            onAnnulQuote={setAnnulQuote}
          />
          {!readOnly && <AttachmentsCard lead={lead} canUpload={canWrite} />}
          <ContactCard lead={lead} active={active} canEdit={canWrite} />
          <CustomFieldsCard lead={lead} canEdit={canWrite} />
          <ProfileCard lead={lead} active={active} />
          <QualificationCard lead={lead} active={active} />
          <OriginCard lead={lead} />
          {(lead.contactLeads.length > 0 || lead.duplicates.length > 0) && (
            <DuplicatesCard lead={lead} readOnly={readOnly} />
          )}
        </div>
      </div>

      {dialog === 'tags' && (
        <TagsDialog
          leadId={lead.id}
          companyId={lead.companyId}
          current={lead.tags}
          canManage={isManager}
          onClose={() => setDialog(null)}
        />
      )}
      <QuoteDialog
        leadId={lead.id}
        companyId={lead.companyId}
        projectId={lead.projectId}
        open={dialog === 'quote'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <StageMoveDialog
        leadId={lead.id}
        companyId={lead.companyId}
        projectId={lead.projectId}
        stage={moveStage}
        onOpenChange={(o) => !o && setMoveStage(null)}
      />
      <StageUndoDialog
        leadId={lead.id}
        companyId={lead.companyId}
        currentStage={lead.pipelineStage}
        open={dialog === 'undo'}
        canAnnul={canAnnul}
        liveDealCount={liveDeals.length}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      {/* Con más de un proceso vivo, «Desistir» no cabe como siguiente paso: los diálogos lo avisan y solo devuelven. */}
      <DealActionDialog
        companyId={lead.companyId}
        deal={dealAction?.deal ?? null}
        mode={dealAction?.mode ?? 'refund'}
        canAnnul={canAnnul}
        initialNext={dealAction?.next ?? 'TASK'}
        liveDealCount={liveDeals.length}
        onOpenChange={(o) => !o && setDealAction(null)}
      />
      <EditDealDialog
        companyId={lead.companyId}
        deal={editDeal}
        onOpenChange={(o) => !o && setEditDeal(null)}
      />
      <EditQuoteDialog
        companyId={lead.companyId}
        quote={editQuote}
        onOpenChange={(o) => !o && setEditQuote(null)}
      />
      <AnnulQuoteDialog
        companyId={lead.companyId}
        quote={annulQuote}
        liveDealCount={liveDeals.length}
        onOpenChange={(o) => !o && setAnnulQuote(null)}
      />

      <InteractionDialog
        leadId={lead.id}
        companyId={lead.companyId}
        mode="log"
        leadActive={active}
        open={dialog === 'log'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <InteractionDialog
        leadId={lead.id}
        companyId={lead.companyId}
        mode="schedule"
        leadActive={active}
        open={dialog === 'schedule'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <CompleteTaskDialog
        task={task}
        leadId={lead.id}
        companyId={lead.companyId}
        leadActive={active}
        open={dialog === 'complete'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <ConfirmAppointmentDialog
        task={task}
        initialOutcome={attendance}
        leadId={lead.id}
        companyId={lead.companyId}
        open={dialog === 'attendance'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <AnnulVisitDialog
        visit={task}
        leadId={lead.id}
        open={dialog === 'annul-visit'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <RescheduleDialog
        task={task}
        leadId={lead.id}
        open={dialog === 'reschedule'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <CancelTaskDialog
        task={task}
        leadId={lead.id}
        companyId={lead.companyId}
        open={dialog === 'cancel'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <AssignDialog
        leadId={lead.id}
        currentAdvisorId={lead.advisorId}
        advisors={advisors ?? []}
        open={dialog === 'assign'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <DesistDialog
        leadId={lead.id}
        companyId={lead.companyId}
        open={dialog === 'desist'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <ReopenDialog
        leadId={lead.id}
        open={dialog === 'reopen'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
    </div>
  );
}

/** Lead conservado si este se cerró por una fusión de personas (G2 lo deja en `externalIds.mergedInto`). */
function mergedLeadOf(lead: Pick<LeadDetail, 'externalIds'>): string | null {
  const value = lead.externalIds.mergedInto;
  return typeof value === 'string' && value ? value : null;
}

/** `mergedInto` es la marca de una fusión, no un id externo: la cabecera la muestra como pista propia. */
function externalIdsToShow(ids: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(ids).filter(([key]) => key !== 'mergedInto'));
}

function Header({
  lead,
  active,
  readOnly,
  isManager,
  canWrite,
  liveDeal,
  canRefund,
  tel,
  wa,
  onAction,
  onRefundAndDesist,
}: {
  lead: LeadDetail;
  active: boolean;
  /** P18: el asesor de captación ve la ficha sin acciones. */
  readOnly: boolean;
  isManager: boolean;
  canWrite: boolean;
  /** Proceso vivo (venta o separación): con uno, «Desistir» se reemplaza por «Devolver y desistir» (P1). */
  liveDeal: DealItem | null;
  canRefund: boolean;
  tel: string | null;
  wa: string | null;
  onAction: (d: DialogKind) => void;
  onRefundAndDesist: () => void;
}) {
  // Cerrado: solo se reabre un lead desistido o cerrado sin venta ni fusión. «Vendido» es `processStatus`
  // (COMPRADOR), no `hasDesisted`, que también está en falso en un lead fusionado (misma regla que la API).
  const mergedInto = mergedLeadOf(lead);
  const sold = lead.processStatus === 'COMPRADOR';
  // Un comprador no es un lead perdido (P24): insignia verde con la fecha de la venta viva.
  const saleAt = lead.deals.find((d) => d.type === 'VENTA' && d.state === 'ACTIVO')?.finishedAt;
  const scrollToCommercial = () =>
    document
      .getElementById('commercial-card')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4 md:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight" data-testid="lead-name">
                {lead.contact.fullName}
              </h1>
              <StageBadge stage={lead.pipelineStage} />
              <ContactBadge
                state={lead.contactState}
                attempts={lead.contactAttempts}
                reason={lead.lastNonEffectiveReasonLabel ?? lead.lastNonEffectiveReason}
              />
              <FollowupBadge
                state={lead.followup.state}
                band={lead.followup.band}
                days={lead.followup.days}
              />
              {lead.isRecontact && <Badge tone="muted">Recontacto</Badge>}
              {lead.lastRecontactAt && (
                <Badge tone="muted" title="Última vez que la persona volvió a registrarse">
                  Volvió a registrarse el {fmt.dateTime(lead.lastRecontactAt)}
                </Badge>
              )}
              {lead.contact.isTest && <Badge tone="danger">Prueba</Badge>}
              {lead.attention && (
                <Link
                  to={`/atencion?companyId=${lead.companyId}`}
                  title="Ver en el tablero del bot"
                >
                  <AttentionBadge attention={lead.attention} />
                </Link>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {lead.projectName} · {lead.companyName} · captado {fmt.dateTime(lead.capturedAt)} por{' '}
              {lead.medium ?? 'medio desconocido'}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-1" data-testid="lead-tags">
              {lead.tags.map((t) => (
                <TagChip key={t.id} tag={t} />
              ))}
              {canWrite && (
                <button
                  type="button"
                  className="text-primary inline-flex items-center gap-1 text-xs underline"
                  onClick={() => onAction('tags')}
                  data-testid="btn-lead-tags"
                >
                  <Tag className="h-3 w-3" /> {lead.tags.length ? 'Etiquetas' : 'Agregar etiqueta'}
                </button>
              )}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1">
                <UserCog className="text-muted-foreground h-3.5 w-3.5" />
                {lead.advisorName ?? <span className="text-warning">Sin asesor</span>}
                {lead.captureAdvisorName && (
                  <span className="text-muted-foreground text-xs" data-testid="capture-advisor">
                    · Captó: {lead.captureAdvisorName}
                  </span>
                )}
                {isManager && (
                  <button
                    type="button"
                    className="text-primary ml-1 text-xs underline"
                    onClick={() => onAction('assign')}
                  >
                    cambiar
                  </button>
                )}
              </span>
              <span className="text-muted-foreground">Sub-estado: {lead.subStatus ?? '—'}</span>
              <span className="text-muted-foreground">
                Interés: {INTEREST_LABEL[lead.interestLevel]}
              </span>
              <ScoreBadge score={lead.score} />
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {tel &&
              (lead.contact.consent.contactable ? (
                <Button asChild variant="outline" size="sm">
                  <a href={tel}>
                    <Phone className="h-4 w-4" /> Llamar
                  </a>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled title={OPT_OUT_NOTICE}>
                  <Phone className="h-4 w-4" /> Llamar
                </Button>
              ))}
            {wa && !readOnly && (
              // P18: en solo lectura no se escribe al cliente desde aquí (plantillas y registro de la gestión).
              <WhatsappMenu
                leadId={lead.id}
                companyId={lead.companyId}
                canManage={isManager}
                optedOut={!lead.contact.consent.contactable}
              />
            )}
            {lead.contact.email && (
              <Button asChild variant="outline" size="sm">
                <a href={`mailto:${lead.contact.email}`}>
                  <Mail className="h-4 w-4" /> Correo
                </a>
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t pt-3">
          {readOnly ? (
            <p
              className="text-muted-foreground inline-flex items-center gap-1.5 self-center text-sm"
              data-testid="read-only-notice"
            >
              <Eye className="h-4 w-4 shrink-0" /> Este lead ahora lo atiende{' '}
              {lead.advisorName ?? 'otro asesor'}; lo ves por haberlo captado.
            </p>
          ) : active ? (
            <>
              <Button size="sm" onClick={() => onAction('log')} data-testid="btn-log">
                <ClipboardList className="h-4 w-4" /> Registrar gestión
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('schedule')}
                data-testid="btn-schedule"
              >
                <CalendarPlus className="h-4 w-4" /> Programar tarea
              </Button>
              {liveDeal ? (
                // Con separación o venta viva no se desiste directamente (la API lo rechaza): primero la
                // devolución, con el desistimiento como siguiente paso (Lote 5 · H1, P1).
                canRefund && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={onRefundAndDesist}
                    data-testid="btn-refund-desist"
                  >
                    <XCircle className="h-4 w-4" /> Devolver y desistir
                  </Button>
                )
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => onAction('desist')}
                  data-testid="btn-desist"
                >
                  <XCircle className="h-4 w-4" /> Desistir
                </Button>
              )}
            </>
          ) : (
            <>
              {sold && !mergedInto ? (
                <Badge tone="success" data-testid="buyer-badge">
                  Comprador{saleAt ? ` · venta del ${fmt.date(saleAt)}` : ''}
                </Badge>
              ) : (
                <Badge tone="danger">
                  Cerrado{lead.desistReason ? ` · ${lead.desistReason}` : ''}{' '}
                  {lead.desistedAt ? `· ${fmt.date(lead.desistedAt)}` : ''}
                </Badge>
              )}
              {mergedInto ? (
                // Fusionado (G2): la persona ya tenía otro lead activo en el proyecto; se trabaja el conservado (la
                // API responde REOPEN_MERGED).
                <span
                  className="text-muted-foreground self-center text-xs"
                  data-testid="reopen-merged-hint"
                >
                  Este lead se fusionó con otro: trabaja el{' '}
                  <Link to={`/leads/${mergedInto}`} className="text-primary underline">
                    lead conservado
                  </Link>
                  .
                </span>
              ) : sold ? (
                // Cerrado por venta (comprador): no se reabre desde aquí (la API responde REOPEN_SOLD); la caída se
                // registra como devolución o anulación de la venta (en la tarjeta Comercial de esta ficha, para
                // quien puede), que es lo que reabre el lead.
                <span
                  className="text-muted-foreground self-center text-xs"
                  data-testid="reopen-sold-hint"
                >
                  Cerrado por venta: si la operación cayó, registra la devolución o la anulación de
                  la venta en la{' '}
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={scrollToCommercial}
                  >
                    tarjeta Comercial
                  </button>{' '}
                  de esta ficha y el lead se reabre solo.
                </span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => onAction('reopen')}>
                  <RotateCcw className="h-4 w-4" /> Reabrir
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onAction('log')}>
                <StickyNote className="h-4 w-4" /> Agregar nota
              </Button>
            </>
          )}
          <span className="text-muted-foreground ml-auto self-center text-xs">
            Primer contacto:{' '}
            {lead.sla.minutes === null
              ? `${fmt.minutes(lead.sla.elapsedMinutes)} sin contacto`
              : `${fmt.minutes(lead.sla.minutes)} (${lead.sla.withinSla ? 'atención a tiempo' : 'atención tarde'})`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function NextTaskPanel({
  lead,
  active,
  onComplete,
  onAttendance,
  onReschedule,
  onCancel,
  onSchedule,
}: {
  lead: LeadDetail;
  active: boolean;
  onComplete: (t: InteractionItem) => void;
  /** E1: una cita no se «completa»: se confirma la asistencia (o se registra que no vino). */
  onAttendance: (t: InteractionItem, outcome: AttendanceOutcome | null) => void;
  onReschedule: (t: InteractionItem) => void;
  onCancel: (t: InteractionItem) => void;
  onSchedule: () => void;
}) {
  if (!active) return null;
  const t = lead.nextTask;
  const overdue = t?.scheduledAt ? new Date(t.scheduledAt).getTime() < Date.now() : false;
  const isAppointment = t?.type === 'SE_CITO';
  const due = !t || appointmentDue(t.scheduledAt);
  return (
    <Card
      className={
        t ? (overdue ? 'border-amber-300' : 'border-primary/40') : 'border-red-300 bg-red-50/40'
      }
      data-testid="next-task"
    >
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        {t ? (
          <>
            <div className="flex-1">
              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                Próxima tarea
              </p>
              <p className="font-medium">
                {TYPE_LABEL[t.type]} · {fmt.dateTimeFull(t.scheduledAt)}
                {overdue && <span className="text-warning ml-2 text-sm">(vencida)</span>}
              </p>
              {t.appointment && (
                <p className="text-sm" data-testid="next-task-appointment">
                  {describeAppointment(t.appointment)}
                  {t.appointment.url && (
                    <>
                      {' · '}
                      <a
                        href={t.appointment.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        abrir enlace
                      </a>
                    </>
                  )}
                </p>
              )}
              {t.description && <p className="text-muted-foreground text-sm">{t.description}</p>}
              <p className="text-muted-foreground text-xs">Responsable: {t.advisorName ?? '—'}</p>
              {t.type === 'SE_CITO' && t.appointment && (
                <AppointmentConfirmations lead={lead} task={t} />
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {isAppointment ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => onAttendance(t, null)}
                    disabled={!due}
                    title={
                      due ? undefined : `Disponible desde el ${fmt.dateTimeFull(t.scheduledAt)}`
                    }
                    data-testid="btn-attendance"
                  >
                    <Check className="h-4 w-4" /> Confirmar asistencia
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAttendance(t, 'NO_ASISTIO')}
                    disabled={!due}
                    title={
                      due ? undefined : `Disponible desde el ${fmt.dateTimeFull(t.scheduledAt)}`
                    }
                    data-testid="btn-no-show"
                  >
                    No asistió / reprogramar
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => onComplete(t)}>
                  <Check className="h-4 w-4" /> Completar
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReschedule(t)}
                aria-label="Reprogramar"
              >
                <CalendarClock className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onCancel(t)}
                aria-label="Cancelar tarea"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-1 items-center gap-2 text-sm">
              <AlertTriangle className="text-danger h-5 w-5" />
              <div>
                <p className="text-danger font-medium">Este lead no tiene próximo paso</p>
                <p className="text-muted-foreground">
                  Programa una tarea o registra una gestión para que no quede sin seguimiento.
                </p>
              </div>
            </div>
            <Button size="sm" onClick={onSchedule}>
              <CalendarPlus className="h-4 w-4" /> Programar tarea
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 7.22: casillas «cita concretada» del asesor digital y del asesor de sala. La casilla que completa la confirmación
 * abre el diálogo con el siguiente paso; las demás marcan o desmarcan al instante.
 */
function AppointmentConfirmations({ lead, task }: { lead: LeadDetail; task: InteractionItem }) {
  const confirm = useConfirmAppointment(lead.id);
  const toast = useToast();
  const [dialog, setDialog] = useState<'DIGITAL' | 'SALA' | null>(null);
  const a = task.appointment!;
  const c = a.confirmations;
  const rows: Array<{
    role: 'DIGITAL' | 'SALA';
    label: string;
    who: string | null;
    mark: typeof c.digital;
    can: boolean;
  }> = [
    {
      role: 'DIGITAL',
      label: c.needsDouble ? 'Asesor digital' : 'Cita concretada',
      who: lead.advisorName,
      mark: c.digital,
      can: c.canConfirm.digital,
    },
  ];
  if (c.needsDouble)
    rows.push({
      role: 'SALA',
      label: 'Asesor de sala',
      who: a.roomAdvisorName,
      mark: c.room,
      can: c.canConfirm.room,
    });
  // Las marcas se habilitan desde la hora programada de la cita (la API también lo exige).
  const due = appointmentDue(task.scheduledAt);
  const toggle = (role: 'DIGITAL' | 'SALA', checked: boolean) => {
    const other = role === 'DIGITAL' ? c.room : c.digital;
    const completes = checked && (!c.needsDouble || !!other);
    if (completes) {
      setDialog(role);
      return;
    }
    confirm
      .mutateAsync({ id: task.id, input: { role, confirmed: checked } })
      .then(() => toast.success(checked ? 'Confirmación registrada' : 'Confirmación retirada'))
      .catch((e: Error) => toast.error('No se pudo registrar', e.message));
  };
  return (
    <div className="mt-2 space-y-1" data-testid="appointment-confirmations">
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
        Cita concretada
      </p>
      {rows.map((r) => (
        <label key={r.role} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={!!r.mark}
            disabled={!r.can || !due || confirm.isPending}
            onChange={(e) => toggle(r.role, e.target.checked)}
            data-testid={r.role === 'DIGITAL' ? 'appt-confirm-digital' : 'appt-confirm-room'}
          />
          <span>
            {r.label}
            {r.who ? `: ${r.who}` : ''}
            {r.mark && (
              <span className="text-muted-foreground text-xs">
                {' '}
                · confirmó {r.mark.byName ?? '—'} {fmt.ago(r.mark.at)}
              </span>
            )}
          </span>
        </label>
      ))}
      {!due && (
        <p className="text-muted-foreground text-xs" data-testid="appt-confirm-available-from">
          Disponible desde el {fmt.dateTimeFull(task.scheduledAt)}.
        </p>
      )}
      {c.needsDouble && (
        <p className="text-muted-foreground text-xs">
          Con las dos confirmaciones se registra la visita y el lead pasa al asesor de sala.
        </p>
      )}
      <ConfirmAppointmentDialog
        task={task}
        role={dialog ?? 'DIGITAL'}
        leadId={lead.id}
        companyId={lead.companyId}
        open={dialog !== null}
        onOpenChange={(o) => !o && setDialog(null)}
      />
    </div>
  );
}

const TYPE_ICON = {
  LLAMADA: Phone,
  WHATSAPP: MessageCircle,
  MAIL: Mail,
  SMS: MessageCircle,
  REUNION: UserCog,
  VISITA: Check,
  SE_CITO: CalendarClock,
  PROFORMA: FileText,
  NOTA: StickyNote,
} as const;

function Timeline({
  lead,
  readOnly,
  onComplete,
  onAttendance,
  onCancel,
  onAnnulVisit,
}: {
  lead: LeadDetail;
  /** P18: sin acciones sobre las tareas; «Anular visita» sigue si la API lo permite a quien mira (E1). */
  readOnly: boolean;
  onComplete: (t: InteractionItem) => void;
  /** E1: una cita pendiente se confirma (asistió / no asistió), no se «completa». */
  onAttendance: (t: InteractionItem) => void;
  /** Una cita de simulación del bot no se completa (la API responde 400): solo se cancela. */
  onCancel: (t: InteractionItem) => void;
  /** E1: anular una visita registrada por error (24 h, motivo). */
  onAnnulVisit: (t: InteractionItem) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actividad</CardTitle>
      </CardHeader>
      <CardContent>
        {lead.interactions.length === 0 && (
          <p className="text-muted-foreground text-sm">Aún no hay gestiones registradas.</p>
        )}
        <ol className="space-y-3">
          {lead.interactions.map((i) => {
            const Icon = TYPE_ICON[i.type];
            const pending = i.status === 'PENDIENTE';
            const summary = (i.metadata.summary as string | undefined) ?? null;
            const annulled = i.type === 'VISITA' && i.status === 'CANCELADA';
            return (
              <li key={i.id} className="flex gap-3" data-testid="timeline-item">
                <div
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${pending ? 'bg-primary/10 text-primary' : i.status === 'CANCELADA' ? 'bg-muted text-muted-foreground' : 'bg-secondary'}`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 border-b pb-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{TYPE_LABEL[i.type]}</span>
                    {pending && (
                      <Badge tone="primary">Pendiente · {fmt.dateTime(i.scheduledAt)}</Badge>
                    )}
                    {i.status === 'CANCELADA' &&
                      (annulled ? (
                        <Badge tone="danger" data-testid="timeline-annulled">
                          Anulada
                        </Badge>
                      ) : (
                        <Badge tone="muted">Cancelada</Badge>
                      ))}
                    {i.status === 'EXPIRADA' && <Badge tone="danger">Expirada</Badge>}
                    {i.result === 'EFECTIVO' && <Badge tone="success">Efectivo</Badge>}
                    {i.result === 'NO_EFECTIVO' && (
                      <Badge tone="warning">
                        No efectivo{i.nonEffectiveReason ? ` · ${i.nonEffectiveReason}` : ''}
                      </Badge>
                    )}
                    {i.visitAt && i.type !== 'VISITA' && (
                      <Badge tone="success">Visita {fmt.dateTime(i.visitAt)}</Badge>
                    )}
                    {(i.origin === 'BOT' || i.createdByKind === 'BOT') && <BotBadge />}
                    {i.simulated && (
                      <Badge tone="warning" data-testid="timeline-simulated">
                        Simulación · no cuenta
                      </Badge>
                    )}
                    {i.origin === 'TRANSITO' && <Badge tone="outline">Tránsito</Badge>}
                    {i.unitCode && <Badge tone="outline">Unidad {i.unitCode}</Badge>}
                    {i.appointment && (
                      <Badge tone="outline">{describeAppointment(i.appointment)}</Badge>
                    )}
                  </div>
                  {i.description && (
                    <p className="mt-1 whitespace-pre-line text-sm">{i.description}</p>
                  )}
                  {summary && (
                    <p className="text-muted-foreground mt-1 text-sm italic">{summary}</p>
                  )}
                  <p className="text-muted-foreground mt-1 text-xs">
                    {pending
                      ? `programada por ${i.createdByName ?? '—'}`
                      : `${fmt.dateTime(i.completedAt ?? i.createdAt)} · ${i.createdByName ?? i.advisorName ?? '—'}`}
                  </p>
                  {pending &&
                    lead.status === 'ACTIVO' &&
                    !readOnly &&
                    (i.simulated ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => onCancel(i)}
                        data-testid="timeline-cancel-simulated"
                      >
                        <XCircle className="h-4 w-4" /> Cancelar
                      </Button>
                    ) : i.type === 'SE_CITO' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => onAttendance(i)}
                        disabled={!appointmentDue(i.scheduledAt)}
                        title={
                          appointmentDue(i.scheduledAt)
                            ? undefined
                            : `Disponible desde el ${fmt.dateTimeFull(i.scheduledAt)}`
                        }
                      >
                        <Check className="h-4 w-4" /> Confirmar asistencia
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => onComplete(i)}
                      >
                        <Check className="h-4 w-4" /> Completar
                      </Button>
                    ))}
                  {i.canAnnul && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger mt-2"
                      onClick={() => onAnnulVisit(i)}
                      data-testid="timeline-annul-visit"
                    >
                      <Ban className="h-4 w-4" /> Anular visita
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

const HISTORY_LABEL: Record<string, string> = {
  ESTADO: 'Estado',
  SUB_ESTADO: 'Sub-estado',
  ETAPA: 'Etapa',
  ASESOR: 'Asesor',
  TIPO_ORIGEN: 'Tipo',
  DESISTIMIENTO: 'Desistimiento',
  REAPERTURA: 'Reapertura',
  ATENCION: 'Atención del bot',
  DATOS_CLIENTE: 'Datos del cliente',
};

/** «Asesor: Ana Torres → Jorge Rojas (Cita concretada)»: nombres resueltos por la API en vez de ids (ux-ficha-07). */
function advisorHistoryText(h: LeadDetail['history'][number]) {
  const name = (value: string | null, resolved: string | null) =>
    !value ? 'sin asesor (bolsa)' : (resolved ?? 'asesor');
  return `Asesor: ${h.fromValue ? `${name(h.fromValue, h.fromName)} → ` : ''}${name(h.toValue, h.toName)}${h.reason ? ` (${h.reason})` : ''}`;
}

function HistoryCard({ lead }: { lead: LeadDetail }) {
  const rows = [
    ...lead.history.map((h) => ({
      id: h.id,
      at: h.createdAt,
      text:
        h.kind === 'ASESOR'
          ? advisorHistoryText(h)
          : `${HISTORY_LABEL[h.kind] ?? h.kind}: ${h.fromValue ? `${label(h.kind, h.fromValue)} → ` : ''}${h.kind === 'ATENCION' && !h.toValue ? 'fuera del bot' : label(h.kind, h.toValue)}${h.reason ? ` (${h.reason})` : ''}`,
      by: h.changedByName,
    })),
    ...lead.scoreHistory.map((s) => ({
      id: s.id,
      at: s.createdAt,
      text: `Puntaje: ${s.fromScore} → ${s.toScore}${s.reason ? ` (${s.reason})` : ''}`,
      by: s.actorType === 'BOT' ? `${s.actorName ?? 'Bot'} (bot)` : s.actorName,
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de cambios</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 text-sm">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap justify-between gap-2 border-b py-1 last:border-0"
            >
              <span>{r.text}</span>
              <span className="text-muted-foreground text-xs">
                {fmt.dateTime(r.at)} · {r.by ?? 'sistema'}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function label(kind: string, value: string | null) {
  if (!value) return '—';
  if (kind === 'ETAPA') return STAGE_LABEL[value as keyof typeof STAGE_LABEL] ?? value;
  if (kind === 'ATENCION') {
    const [owner, state] = value.split(':');
    return `${owner === 'HUMAN' ? 'Asesor (bot en pausa)' : 'Bot'} · ${ATTENTION_STATE_LABELS[state ?? ''] ?? state}`;
  }
  return value;
}

function ContactCard({
  lead,
  active,
  canEdit,
}: {
  lead: LeadDetail;
  active: boolean;
  /** Puede editar los datos del cliente (también con el lead cerrado); false en solo lectura (P18). */
  canEdit: boolean;
}) {
  const toast = useToast();
  const consent = useSetConsent(lead.id);
  const c = lead.contact;
  const [editing, setEditing] = useState(false);
  // El opt-out se confirma en un diálogo propio (OptOutDialog): cancelar no registra nada.
  const [optingOut, setOptingOut] = useState(false);
  const copy = (v: string) => {
    void navigator.clipboard?.writeText(v);
    toast.success('Copiado');
  };
  const [granting, setGranting] = useState(false);
  return (
    <Card className={!c.consent.contactable ? 'border-red-300' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          Contacto
          {c.consent.contactable ? (
            <Badge tone={c.consent.consentAt ? 'success' : 'muted'} data-testid="consent-badge">
              <ShieldCheck className="h-3 w-3" />
              {c.consent.consentAt
                ? `Consentimiento ${c.consent.consentSource ?? ''} · ${fmt.date(c.consent.consentAt)}`
                : 'Sin consentimiento registrado'}
            </Badge>
          ) : (
            <Badge tone="danger" data-testid="consent-badge">
              <ShieldOff className="h-3 w-3" /> No contactar · {fmt.date(c.consent.optOutAt)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!c.consent.contactable && (
          <p className="text-danger text-xs">
            Pidió que no le escribamos{c.consent.optOutReason ? `: ${c.consent.optOutReason}` : ''}.
            WhatsApp y el bot quedan bloqueados hasta un nuevo consentimiento explícito.
          </p>
        )}
        <Row label="Celular">
          {c.phone ? (
            <span className="inline-flex items-center gap-1">
              {c.phone}
              <button
                type="button"
                onClick={() => copy(c.phoneNormalized ?? c.phone!)}
                aria-label="Copiar teléfono"
              >
                <Copy className="text-muted-foreground h-3 w-3" />
              </button>
            </span>
          ) : (
            '—'
          )}
        </Row>
        {c.altPhones.length > 0 && (
          <Row label="Otros celulares">
            <span data-testid="contact-alt-phones">{c.altPhones.join(' · ')}</span>
          </Row>
        )}
        <Row label="Correo">{c.email ?? '—'}</Row>
        {c.altEmails.length > 0 && (
          <Row label="Otros correos">
            <span data-testid="contact-alt-emails">{c.altEmails.join(' · ')}</span>
          </Row>
        )}
        <Row label="Documento">
          {c.documentNumber ? `${c.documentType ?? ''} ${c.documentNumber}` : '—'}
        </Row>
        {c.altDocuments.length > 0 && (
          <Row label="Otros documentos">{c.altDocuments.join(' · ')}</Row>
        )}
        <Row label="Distrito">
          <span data-testid="contact-district">{c.district ?? '—'}</span>
        </Row>
        {c.occupation && <Row label="Ocupación">{c.occupation}</Row>}
        {c.birthDate && <Row label="Nacimiento">{fmt.date(c.birthDate)}</Row>}
        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="contact-edit"
            >
              <Pencil className="h-3.5 w-3.5" /> Editar datos del cliente
            </Button>
          </div>
        )}
        <ContactEditDialog lead={lead} open={editing} onOpenChange={setEditing} />
        <OptOutDialog lead={lead} open={optingOut} onOpenChange={setOptingOut} />
        <ConsentDialog mode="grant" lead={lead} open={granting} onOpenChange={setGranting} />
        {active && (
          <div className="flex flex-wrap gap-2 pt-1">
            {c.consent.contactable ? (
              <>
                {!c.consent.consentAt && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setGranting(true)}
                    disabled={consent.isPending}
                    data-testid="btn-consent"
                  >
                    <ShieldCheck className="h-4 w-4" /> Registrar consentimiento
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => setOptingOut(true)}
                  disabled={consent.isPending}
                  data-testid="btn-optout"
                >
                  <ShieldOff className="h-4 w-4" /> Marcar «no contactar»
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setGranting(true)}
                disabled={consent.isPending}
                data-testid="btn-consent"
              >
                <ShieldCheck className="h-4 w-4" /> Registrar nuevo consentimiento
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const HORIZONS = Object.values(PurchaseHorizon);
const PURPOSES = Object.values(PurchasePurpose);

/** Perfil de compra: presupuesto, dormitorios, tipología, forma de pago, plazo y finalidad (editable, también por el bot). */
function ProfileCard({ lead, active }: { lead: LeadDetail; active: boolean }) {
  const mutation = useUpdateLead(lead.id);
  const toast = useToast();
  const p = lead.profile;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    budgetMin: p.budgetMin?.toString() ?? '',
    budgetMax: p.budgetMax?.toString() ?? '',
    budgetCurrency: p.budgetCurrency,
    bedroomsWanted: p.bedroomsWanted?.toString() ?? '',
    typologyWanted: p.typologyWanted ?? '',
    financingWanted: p.financingWanted ?? '',
    purchaseHorizon: p.purchaseHorizon ?? '',
    purchasePurpose: p.purchasePurpose ?? '',
  });
  useEffect(() => {
    setF({
      budgetMin: p.budgetMin?.toString() ?? '',
      budgetMax: p.budgetMax?.toString() ?? '',
      budgetCurrency: p.budgetCurrency,
      bedroomsWanted: p.bedroomsWanted?.toString() ?? '',
      typologyWanted: p.typologyWanted ?? '',
      financingWanted: p.financingWanted ?? '',
      purchaseHorizon: p.purchaseHorizon ?? '',
      purchasePurpose: p.purchasePurpose ?? '',
    });
  }, [p]);
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await mutation.mutateAsync({
        profile: {
          budgetMin: f.budgetMin ? Number(f.budgetMin) : null,
          budgetMax: f.budgetMax ? Number(f.budgetMax) : null,
          budgetCurrency: f.budgetCurrency,
          bedroomsWanted: f.bedroomsWanted ? Number(f.bedroomsWanted) : null,
          typologyWanted: f.typologyWanted || null,
          financingWanted: (f.financingWanted || null) as FinancingType | null,
          purchaseHorizon: (f.purchaseHorizon || null) as PurchaseHorizon | null,
          purchasePurpose: (f.purchasePurpose || null) as PurchasePurpose | null,
        },
      });
      toast.success('Perfil de compra actualizado');
      setEditing(false);
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const empty =
    p.budgetMin === null &&
    p.budgetMax === null &&
    p.bedroomsWanted === null &&
    !p.typologyWanted &&
    !p.financingWanted &&
    !p.purchaseHorizon;
  return (
    <Card data-testid="profile-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Perfil de compra
          {active && !editing && (
            <button
              type="button"
              className="text-primary text-xs underline"
              onClick={() => setEditing(true)}
            >
              {empty ? 'completar' : 'editar'}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {!editing ? (
          <div className="space-y-2">
            {empty && (
              <p className="text-muted-foreground text-xs">
                Sin datos aún: lo completa el asesor o el bot durante la conversación.
              </p>
            )}
            <Row label="Presupuesto">
              {p.budgetMin === null && p.budgetMax === null
                ? '—'
                : `${fmt.money(p.budgetMin, p.budgetCurrency)} – ${fmt.money(p.budgetMax, p.budgetCurrency)}`}
            </Row>
            <Row label="Dormitorios">{p.bedroomsWanted ?? '—'}</Row>
            <Row label="Tipología">{p.typologyWanted ?? '—'}</Row>
            <Row label="Forma de pago">
              {p.financingWanted ? FINANCING_LABELS[p.financingWanted] : '—'}
            </Row>
            <Row label="Plazo">
              {p.purchaseHorizon ? PURCHASE_HORIZON_LABELS[p.purchaseHorizon] : '—'}
            </Row>
            <Row label="Finalidad">
              {p.purchasePurpose ? PURCHASE_PURPOSE_LABELS[p.purchasePurpose] : '—'}
            </Row>
            {p.updatedAt && (
              <p className="text-muted-foreground text-xs">Actualizado {fmt.ago(p.updatedAt)}</p>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="pf-min" className="text-xs">
                Presupuesto mín.
              </Label>
              <Input
                id="pf-min"
                type="number"
                min={0}
                value={f.budgetMin}
                onChange={(e) => set({ budgetMin: e.target.value })}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-max" className="text-xs">
                Presupuesto máx.
              </Label>
              <Input
                id="pf-max"
                type="number"
                min={0}
                value={f.budgetMax}
                onChange={(e) => set({ budgetMax: e.target.value })}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-cur" className="text-xs">
                Moneda
              </Label>
              <Select
                id="pf-cur"
                value={f.budgetCurrency}
                onChange={(e) => set({ budgetCurrency: e.target.value as 'USD' | 'PEN' })}
                className="h-9"
              >
                <option value="USD">US$</option>
                <option value="PEN">S/</option>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-bed" className="text-xs">
                Dormitorios
              </Label>
              <Input
                id="pf-bed"
                type="number"
                min={0}
                max={20}
                value={f.bedroomsWanted}
                onChange={(e) => set({ bedroomsWanted: e.target.value })}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-typ" className="text-xs">
                Tipología
              </Label>
              <Input
                id="pf-typ"
                value={f.typologyWanted}
                onChange={(e) => set({ typologyWanted: e.target.value })}
                placeholder="2D, 3D+E…"
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-fin" className="text-xs">
                Forma de pago
              </Label>
              <Select
                id="pf-fin"
                value={f.financingWanted}
                onChange={(e) => set({ financingWanted: e.target.value })}
                className="h-9"
              >
                <option value="">—</option>
                {Object.values(FinancingType).map((v) => (
                  <option key={v} value={v}>
                    {FINANCING_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-hor" className="text-xs">
                Plazo
              </Label>
              <Select
                id="pf-hor"
                value={f.purchaseHorizon}
                onChange={(e) => set({ purchaseHorizon: e.target.value })}
                className="h-9"
              >
                <option value="">—</option>
                {HORIZONS.map((v) => (
                  <option key={v} value={v}>
                    {PURCHASE_HORIZON_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pf-pur" className="text-xs">
                Finalidad
              </Label>
              <Select
                id="pf-pur"
                value={f.purchasePurpose}
                onChange={(e) => set({ purchasePurpose: e.target.value })}
                className="h-9"
              >
                <option value="">—</option>
                {PURPOSES.map((v) => (
                  <option key={v} value={v}>
                    {PURCHASE_PURPOSE_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={mutation.isPending}
                data-testid="profile-save"
              >
                Guardar
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function QualificationCard({ lead, active }: { lead: LeadDetail; active: boolean }) {
  const { data: catalogs } = useCatalogs(lead.companyId);
  const mutation = useUpdateLead(lead.id);
  const toast = useToast();
  const save = (patch: Parameters<typeof mutation.mutateAsync>[0]) =>
    mutation
      .mutateAsync(patch)
      .then(() => toast.success('Actualizado'))
      .catch((e: Error) => toast.error('No se pudo actualizar', e.message));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Calificación</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="space-y-1">
          <label htmlFor="q-substatus" className="text-muted-foreground text-xs">
            Sub-estado
          </label>
          <Select
            id="q-substatus"
            value={lead.subStatus ?? ''}
            disabled={!active || !catalogs}
            onChange={(e) => void save({ subStatus: e.target.value })}
          >
            {!lead.subStatus && <option value="">—</option>}
            {catalogs?.subStatuses.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="q-interest" className="text-muted-foreground text-xs">
            Nivel de interés
          </label>
          <Select
            id="q-interest"
            value={lead.interestLevel}
            disabled={!active}
            onChange={(e) => void save({ interestLevel: e.target.value as InterestLevel })}
          >
            {Object.values(InterestLevel).map((l) => (
              <option key={l} value={l}>
                {INTEREST_LABEL[l]}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <label htmlFor="q-score" className="text-muted-foreground text-xs">
            Puntaje ({lead.score})
          </label>
          <input
            id="q-score"
            type="range"
            min={0}
            max={100}
            defaultValue={lead.score}
            disabled={!active}
            className="w-full"
            onMouseUp={(e) =>
              void save({
                score: Number((e.target as HTMLInputElement).value),
                scoreReason: 'Ajuste manual',
              })
            }
            onTouchEnd={(e) =>
              void save({
                score: Number((e.target as HTMLInputElement).value),
                scoreReason: 'Ajuste manual',
              })
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}

const ATTR_LABEL: Record<string, string> = {
  platform: 'Plataforma',
  campaignId: 'Campaña (id)',
  campaignName: 'Campaña',
  adsetId: 'Conjunto (id)',
  adsetName: 'Conjunto',
  adId: 'Anuncio (id)',
  adName: 'Anuncio',
  formId: 'Formulario',
  formName: 'Formulario',
  pageId: 'Página',
  sourceUrl: 'URL de origen',
  referrer: 'Referencia',
};

function OriginCard({ lead }: { lead: LeadDetail }) {
  const utm = Object.entries(lead.utm).filter(([, v]) => v);
  const attribution = Object.entries(lead.attribution).filter(([, v]) => v);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Origen y atribución</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Medio original">
          {lead.originalMedium
            ? `${lead.originalMedium} (${lead.originalMediumCategory ?? '—'})`
            : '—'}
        </Row>
        <Row label="Último medio">
          {lead.medium ? `${lead.medium} (${lead.mediumCategory ?? '—'})` : '—'}
        </Row>
        <Row label="Canal">{lead.entryChannel ?? '—'}</Row>
        <Row label="Tipo">{lead.originType}</Row>
        <Row label="Único del mes">{lead.isUniqueLeadMonth ? 'Sí' : 'No'}</Row>
        {utm.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {utm.map(([k, v]) => (
              <Badge key={k} tone="outline" title={`utm_${k}`}>
                {k}: {v}
              </Badge>
            ))}
          </div>
        )}
        {attribution.length > 0 && (
          <div className="space-y-1 border-t pt-2" data-testid="attribution">
            <p className="text-muted-foreground text-xs font-medium uppercase">Atribución</p>
            {attribution.map(([k, v]) => (
              <Row key={k} label={ATTR_LABEL[k] ?? k}>
                {k === 'sourceUrl' ? (
                  <a
                    href={String(v)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary break-all underline"
                  >
                    {String(v)
                      .replace(/^https?:\/\//, '')
                      .slice(0, 40)}
                  </a>
                ) : (
                  <span className="break-all">{String(v)}</span>
                )}
              </Row>
            ))}
          </div>
        )}
        {Object.keys(externalIdsToShow(lead.externalIds)).length > 0 && (
          <p className="text-muted-foreground pt-1 text-xs">
            IDs externos: {JSON.stringify(externalIdsToShow(lead.externalIds))}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DuplicatesCard({ lead, readOnly }: { lead: LeadDetail; readOnly: boolean }) {
  const { user } = useAuth();
  // Fusionar escribe en los dos contactos: nunca desde la ficha en solo lectura (P18).
  const canMerge =
    !readOnly &&
    !!user?.memberships.some(
      (m) => m.companyId === lead.companyId && MANAGER_ROLES.includes(m.role),
    );
  // Contacto que se fusionaría en este (doble confirmación en MergeContactsDialog).
  const [merging, setMerging] = useState<string | null>(null);
  return (
    <Card className="border-amber-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="text-warning h-4 w-4" /> Recontactos y duplicados
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {lead.contactLeads.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs font-medium uppercase">
              Otros leads de esta persona
            </p>
            <ul className="mt-1 space-y-1">
              {lead.contactLeads.map((l) => (
                <li key={l.id}>
                  <Link to={`/leads/${l.id}`} className="hover:underline">
                    {l.projectName}
                  </Link>{' '}
                  <span className="text-muted-foreground">
                    · {STAGE_LABEL[l.pipelineStage]} · {fmt.date(l.capturedAt)} · {l.medium ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {lead.duplicates.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs font-medium uppercase">
              Contactos que comparten datos
            </p>
            <ul className="mt-1 space-y-1">
              {lead.duplicates.map((d) => (
                <li key={d.contactId}>
                  {d.name}{' '}
                  <span className="text-muted-foreground">
                    · coincide por {d.matchedOn.join(', ').toLowerCase()}
                  </span>
                  {d.leads.map((l) => (
                    <Link
                      key={l.id}
                      to={`/leads/${l.id}`}
                      className="text-primary ml-2 hover:underline"
                    >
                      {l.projectName}
                    </Link>
                  ))}
                  {canMerge && (
                    <button
                      type="button"
                      className="text-primary ml-2 text-xs underline"
                      onClick={() => setMerging(d.contactId)}
                      data-testid="merge-btn"
                    >
                      fusionar aquí
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground mt-1 text-xs">
              Fusionar conserva este contacto: los leads, archivos y conversaciones del otro pasan
              aquí y sus datos distintos quedan como alternos.
            </p>
          </div>
        )}
        {merging && (
          <MergeContactsDialog
            open
            targetContactId={lead.contact.id}
            sourceContactIds={[merging]}
            onOpenChange={(open) => !open && setMerging(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}

const QUOTE_TONE: Record<string, 'success' | 'muted' | 'danger' | 'primary'> = {
  VIGENTE: 'success',
  EXPIRADA: 'muted',
  ANULADA: 'danger',
  CONVERTIDA: 'primary',
};
const DEAL_TONE: Record<DealItem['state'], 'success' | 'warning' | 'danger'> = {
  ACTIVO: 'success',
  DEVUELTO: 'warning',
  ANULADO: 'danger',
};
const DEAL_STATE_LABEL: Record<DealItem['state'], string> = {
  ACTIVO: 'Activa',
  DEVUELTO: 'Devuelta',
  ANULADO: 'Anulada',
};

/** Separación cuya venta sigue viva (mismas unidades): se devuelve o anula la venta, no la separación (P3). */
function linkedToLiveSale(deal: DealItem, deals: DealItem[]): boolean {
  if (deal.type !== 'SEPARACION') return false;
  const unitIds = new Set(deal.units.map((u) => u.id));
  return deals.some(
    (s) => s.type === 'VENTA' && s.state === 'ACTIVO' && s.units.some((u) => unitIds.has(u.id)),
  );
}

/**
 * Proformas y procesos del lead con las acciones comerciales (emitir, separar, vender, devolver, anular, retroceder).
 * Devolver / anular un proceso y «Retroceder» desde Separación / Venta no dependen de que el lead esté activo: en
 * un lead cerrado por venta son la única vía para deshacer la venta y reabrirlo (la API no exige ACTIVO ahí); solo
 * dependen de quién mira (`canRefund` / `canAnnul`, que ya excluyen la ficha en solo lectura).
 */
function CommercialCard({
  lead,
  active,
  canRefund,
  canAnnul,
  canEditQuotes,
  onNewQuote,
  onMove,
  onUndo,
  onDealAction,
  onEditDeal,
  onEditQuote,
  onAnnulQuote,
}: {
  lead: LeadDetail;
  active: boolean;
  /** P16: devuelve el asesor dueño o quien gestiona; anula solo quien gestiona (y solo él corrige procesos, P14). */
  canRefund: boolean;
  canAnnul: boolean;
  /** P14: corrige una proforma quien escribe sobre el lead (el asesor arregla la suya); nunca en solo lectura. */
  canEditQuotes: boolean;
  onNewQuote: () => void;
  onMove: (stage: PipelineStage) => void;
  onUndo: () => void;
  onDealAction: (deal: DealItem, mode: DealActionMode) => void;
  onEditDeal: (deal: DealItem) => void;
  onEditQuote: (quote: QuoteItem) => void;
  onAnnulQuote: (quote: QuoteItem) => void;
}) {
  // «Venta» solo desde Proforma / Separación cuando la empresa exige separación o proforma previa (P12).
  const { data: catalogs } = useCatalogs(lead.companyId);
  const allowed = active
    ? allowedStageMoves(lead.pipelineStage, {
        requirePriorSeparationForSale: catalogs?.settings.requirePriorSeparationForSale ?? true,
      })
    : [];
  // Retroceder desde Separación / Venta es devolver o anular: solo para quien puede (P16), también con el lead
  // cerrado por venta; cancelar la cita o anular la proforma, solo con el lead activo.
  const undoKind = stageUndoKind(lead.pipelineStage);
  const canUndoDeal = canRefund || canAnnul;
  const canUndo = !!undoKind && (undoKind === 'REFUND_DEAL' ? canUndoDeal : active);
  // Corregir un proceso (P14): quien gestiona, también con el lead cerrado por venta; nunca uno anulado.
  const canEditDeal = (d: DealItem) => canAnnul && d.state !== 'ANULADO';
  return (
    <Card data-testid="commercial-card" id="commercial-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Comercial
          <span className="text-muted-foreground text-xs font-normal">
            {STAGE_LABEL[lead.pipelineStage]}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {(active || canUndo) && (
          <div className="flex flex-wrap gap-1.5">
            {allowed.includes('PROFORMA') && (
              <Button size="sm" variant="outline" onClick={onNewQuote} data-testid="btn-quote">
                <FileText className="h-4 w-4" /> Proforma
              </Button>
            )}
            {active && lead.pipelineStage === 'PROFORMA' && (
              <Button size="sm" variant="outline" onClick={onNewQuote}>
                <FileText className="h-4 w-4" /> Otra proforma
              </Button>
            )}
            {allowed.includes('SEPARACION') && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMove('SEPARACION')}
                data-testid="btn-separation"
              >
                Separación
              </Button>
            )}
            {allowed.includes('VENTA') && (
              <Button size="sm" onClick={() => onMove('VENTA')} data-testid="btn-sale">
                Venta
              </Button>
            )}
            {canUndo && (
              <Button size="sm" variant="ghost" onClick={onUndo} data-testid="btn-undo">
                <Undo2 className="h-4 w-4" /> Retroceder
              </Button>
            )}
          </div>
        )}
        {lead.quotes.length === 0 && lead.deals.length === 0 && (
          <p className="text-muted-foreground text-xs">Sin proformas ni procesos todavía.</p>
        )}
        {lead.quotes.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs font-medium uppercase">Proformas</p>
            <ul className="mt-1 space-y-1.5">
              {lead.quotes.map((q) => (
                <li key={q.id} className="rounded-md border p-2" data-testid="quote-row">
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <span className="font-medium">
                      {q.code} {q.unitCode ? `· ${q.unitCode}` : '· sin unidad'}
                    </span>
                    <span className="font-semibold">{fmt.money(q.price, q.currency)}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-1 text-xs">
                    <Badge tone={QUOTE_TONE[q.status] ?? 'muted'}>
                      {QUOTE_STATUS_LABELS[q.status] ?? q.status}
                    </Badge>
                    {Number(q.discount) > 0 && (
                      <span>
                        lista {fmt.money(q.priceList, q.currency)} − dscto{' '}
                        {fmt.money(q.discount, q.currency)}
                      </span>
                    )}
                    {q.financingType && <span>· {FINANCING_LABELS[q.financingType]}</span>}
                    {q.expiresAt && q.status === 'VIGENTE' && (
                      <span>· vence {fmt.date(q.expiresAt)}</span>
                    )}
                    <span>· {fmt.date(q.createdAt)}</span>
                    {active && q.status !== 'ANULADA' && q.status !== 'CONVERTIDA' && (
                      <span className="ml-auto flex gap-2">
                        {canEditQuotes && (
                          <button
                            type="button"
                            className="text-primary underline"
                            onClick={() => onEditQuote(q)}
                            data-testid="btn-edit-quote"
                          >
                            <Pencil className="mr-0.5 inline h-3 w-3" />
                            corregir
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-destructive underline"
                          onClick={() => onAnnulQuote(q)}
                          data-testid="btn-annul-quote"
                        >
                          anular
                        </button>
                      </span>
                    )}
                  </div>
                  {q.annulReason && <p className="text-danger text-xs">Anulada: {q.annulReason}</p>}
                  {q.notes && <p className="text-muted-foreground text-xs">{q.notes}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {lead.deals.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs font-medium uppercase">Procesos</p>
            <ul className="mt-1 space-y-1.5">
              {lead.deals.map((d) => (
                <li key={d.id} className="rounded-md border p-2" data-testid="deal-row">
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <span className="font-medium">
                      {d.type === 'VENTA' ? 'Venta' : 'Separación'} ·{' '}
                      {d.units.map((u) => u.code).join(', ') || 'sin unidad'}
                    </span>
                    <Badge tone={DEAL_TONE[d.state]}>{DEAL_STATE_LABEL[d.state]}</Badge>
                  </div>
                  <div className="text-muted-foreground mt-0.5 text-xs">
                    {fmt.date(d.type === 'VENTA' ? d.finishedAt : d.startedAt)}
                    {d.type === 'VENTA'
                      ? ` · ${fmt.money(d.amountTotal, d.currency)}`
                      : d.amountInitial
                        ? ` · reserva ${fmt.money(d.amountInitial, d.initialCurrency)}`
                        : ''}
                    {d.contractMode ? ` · ${FINANCING_LABELS[d.contractMode]}` : ''}
                    {d.bank ? ` · ${d.bank}` : ''}
                    {d.contractNumber ? ` · ${d.contractNumber}` : ''}
                    {d.quoteCode ? ` · ${d.quoteCode}` : ''}
                    {d.advisorName ? ` · ${d.advisorName}` : ''}
                  </div>
                  {d.state === 'DEVUELTO' && (
                    <p className="text-warning text-xs">
                      Devolución {fmt.date(d.refundedAt)} · {d.dropReason}
                      {d.refundNotes ? ` · ${d.refundNotes}` : ''}
                    </p>
                  )}
                  {d.state === 'ANULADO' && (
                    <p className="text-danger text-xs">
                      Anulada {fmt.date(d.annulledAt)}: {d.annulReason}
                    </p>
                  )}
                  {canEditDeal(d) && (
                    <div className="mt-1">
                      <button
                        type="button"
                        className="text-primary text-xs underline"
                        onClick={() => onEditDeal(d)}
                        data-testid="btn-edit-deal"
                      >
                        <Pencil className="mr-0.5 inline h-3 w-3" />
                        corregir
                      </button>
                    </div>
                  )}
                  {d.state === 'ACTIVO' &&
                    canUndoDeal &&
                    (linkedToLiveSale(d, lead.deals) ? (
                      // P3: la separación de una venta viva no se devuelve ni anula por su cuenta (la API responde
                      // SEPARATION_HAS_SALE). Devolver la venta la arrastra; anularla (registro por error) no: la
                      // separación sigue viva y la unidad vuelve a SEPARADO.
                      <p
                        className="text-muted-foreground mt-1 text-xs"
                        data-testid="deal-linked-sale"
                      >
                        Ligada a la venta: si devuelves la venta, esta separación la acompaña; si la
                        anulas, la separación sigue viva.
                      </p>
                    ) : (
                      <div className="mt-1 flex gap-2">
                        {canRefund && (
                          <button
                            type="button"
                            className="text-warning text-xs underline"
                            onClick={() => onDealAction(d, 'refund')}
                            data-testid="btn-refund"
                          >
                            <RotateCcw className="mr-0.5 inline h-3 w-3" />
                            devolución
                          </button>
                        )}
                        {canAnnul && (
                          <button
                            type="button"
                            className="text-destructive text-xs underline"
                            onClick={() => onDealAction(d, 'annul')}
                            data-testid="btn-annul"
                          >
                            <Ban className="mr-0.5 inline h-3 w-3" />
                            anular
                          </button>
                        )}
                      </div>
                    ))}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Atención del bot (Fase 8): quién tiene la conversación, cuándo retoma el bot y los botones para intervenir. */
function AttentionCard({
  lead,
  active,
  isManager,
}: {
  lead: LeadDetail;
  active: boolean;
  isManager: boolean;
}) {
  const action = useAttentionAction();
  const toast = useToast();
  const a = lead.attention;
  const info = lead.attentionInfo;
  if (!a || !info) return null;
  const paused = a.owner === 'HUMAN';
  const run = (kind: 'take' | 'release' | 'stop') => {
    const done = {
      take: 'Conversación tomada: el bot queda en pausa',
      release: 'Conversación devuelta al bot',
      stop: 'Lead retirado del bot',
    }[kind];
    action
      .mutateAsync({ leadId: lead.id, action: kind })
      .then(() => toast.success(done))
      .catch((e: Error) => toast.error('No se pudo aplicar', e.message));
  };
  const resumeIn = info.resumeAt
    ? Math.max(0, Math.round((new Date(info.resumeAt).getTime() - Date.now()) / 60_000))
    : null;
  return (
    <Card
      className={paused ? 'border-warning/60' : 'border-primary/40'}
      data-testid="attention-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          Atención del bot
          <AttentionBadge attention={a} testId="attention-card-badge" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!info.botActive ? (
          <p data-testid="attention-bot-off">
            <span className="font-medium">Bot apagado: el lead sigue contigo.</span>{' '}
            {paused
              ? `La conversación la tiene ${info.pausedByName ?? 'un asesor'} desde ${fmt.ago(a.pausedAt)} y el bot no la retoma mientras esté apagado.`
              : 'El bot no escribe ni responde mientras esté apagado (Administración → Bot de atención); contacta tú al cliente.'}
          </p>
        ) : paused ? (
          <p>
            La conversación la tiene{' '}
            <span className="font-medium">{info.pausedByName ?? 'un asesor'}</span> desde{' '}
            {fmt.ago(a.pausedAt)}. Si no se registra una gestión,{' '}
            <span className="font-medium">
              el bot retoma {resumeIn === 0 ? 'en cualquier momento' : `en ${resumeIn} min`}
            </span>{' '}
            ({fmt.time(info.resumeAt)}). Cada gestión reinicia el plazo de {info.resumeAfterMinutes}{' '}
            min.
          </p>
        ) : (
          <p>
            El bot está atendiendo esta conversación. Cualquier gestión que registres lo pone en
            pausa; también puedes tomarla directamente.
          </p>
        )}
        <Row label="Estado">{ATTENTION_STATE_LABELS[a.state]}</Row>
        <Row label="Intentos sin respuesta">{a.attempts}</Row>
        {a.nextAt && !paused && <Row label="Próximo intento">{fmt.dateTime(a.nextAt)}</Row>}
        {a.note && <Row label="Nota del bot">{a.note}</Row>}
        {active && (
          <div className="flex flex-wrap gap-2 pt-1">
            {!paused && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => run('take')}
                disabled={action.isPending}
                data-testid="btn-attention-take"
              >
                <PauseCircle className="h-4 w-4" /> Tomar la conversación
              </Button>
            )}
            {paused && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => run('release')}
                disabled={action.isPending}
                data-testid="btn-attention-release"
              >
                <Bot className="h-4 w-4" /> Devolver al bot
              </Button>
            )}
            {isManager && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => run('stop')}
                disabled={action.isPending}
                data-testid="btn-attention-stop"
              >
                Retirar del bot
              </Button>
            )}
          </div>
        )}
        <p className="text-muted-foreground text-xs">
          <Link to={`/atencion?companyId=${lead.companyId}`} className="underline">
            Ver el tablero del bot
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
